import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";

import type {IContestStat} from "data/stores/contest_player_stats/contest_player_stats.store";
import type {IRosterStore} from "data/stores/roster/roster.store";
import {Empty} from "data/types/global";
import {compact, map, orderBy, last, shuffle} from "lodash";
import {RankChangeState} from "data/enums";
import type {IGamePlayer, IPlayerCalculations} from "data/utils/player.calculations";
import type {ILocalizationStore} from "data/stores/localization/localization.store";

interface IParams {
	playerId: number | null;
	contestId: number;
}

interface IExtendedPlayer extends IGamePlayer {
	changeState: RankChangeState;
}

export interface IMissedPlayersController extends ViewController<IParams> {
	get missedPlayers(): Empty<IExtendedPlayer>[];
}

@injectable()
export class MissedPlayersController implements IMissedPlayersController {
	@observable private _playerId: number | null = null;
	@observable private _contestId: number = 0;

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.RosterStore) private readonly _rosterStore: IRosterStore,
		@inject(Bindings.PlayerCalculations)
		private readonly _playerCalculations: IPlayerCalculations
	) {
		makeAutoObservable(this);
	}

	get missedPlayers(): Empty<IExtendedPlayer>[] {
		const [firstRandom, secondRandom, thirdRandom] = this.randomPlayers;
		return [this.higherPlayer || firstRandom, secondRandom, this.lowerPlayer || thirdRandom];
	}

	init(param: IParams) {
		this._playerId = param.playerId;
		this._contestId = param.contestId;
	}

	onChange(param: IParams) {
		this._playerId = param.playerId;
		this._contestId = param.contestId;
	}

	playerStats(playerId?: number | null) {
		if (playerId) {
			return this._playerCalculations.getGamePlayerById(playerId, this._contestId);
		}
	}

	private rankChange(points: number = 0) {
		const playerPoints = this.playerStats(this._playerId)?.contestStats?.projectedPoints || 0;

		if (points > playerPoints) {
			return RankChangeState.UP;
		}

		if (points < playerPoints) {
			return RankChangeState.DOWN;
		}

		return RankChangeState.NONE;
	}

	private get playersWithStats(): IExtendedPlayer[] {
		const restPlayerIds = this._rosterStore.restSpinPlayerIds;

		return orderBy(
			compact(
				map(restPlayerIds, (playerId) => {
					const gamePlayer = this._playerCalculations.getGamePlayerById(
						playerId,
						this._contestId
					);

					if (!gamePlayer) {
						return;
					}
					return {
						...gamePlayer,
						changeState: this.rankChange(gamePlayer?.contestStats?.projectedPoints),
					};
				})
			),
			"player.stats.projectedPoints",
			"desc"
		);
	}

	private get selectedPlayerStats(): Empty<IContestStat> {
		const rosterByContestID = this._rosterStore.currentRoster;
		const lastSelectedPlayerId = last(rosterByContestID?.spins)?.playerId || 0;
		return this.playerStats(lastSelectedPlayerId)?.contestStats;
	}

	private get higherPlayer() {
		const playersWithStats = this.playersWithStats;
		const selectedPlayerStats = this.selectedPlayerStats?.projectedPoints || 0;

		return playersWithStats.find((it) => {
			const playerPoints = it.contestStats?.projectedPoints || 0;
			return playerPoints > selectedPlayerStats;
		});
	}

	private get lowerPlayer() {
		// To change sort direction
		const playersWithStats = this.playersWithStats.reverse();
		const selectedPlayerStats = this.selectedPlayerStats?.projectedPoints || 0;

		return playersWithStats.find((it) => {
			const playerPoints = it.contestStats?.projectedPoints || 0;
			return playerPoints < selectedPlayerStats;
		});
	}

	private get randomPlayers() {
		const lowerPlayer = this.lowerPlayer;
		const higherPlayer = this.higherPlayer;
		const filteredPlayers = this.playersWithStats.filter(
			(player) => ![lowerPlayer?.id, higherPlayer?.id].includes(player.id)
		);

		return shuffle(filteredPlayers);
	}
}
